import React, { useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import {
  ChevronDown,
  ChevronUp,
  Download,
  Minus,
  Plus,
  Printer,
} from '@factorialco/f0-react/icons/app'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import LoadingSpinner from './LoadingSpinner'
import ToolbarButton from './ToolbarButton'
import './index.styles.css'
import { downloadPdf, printPdf } from './pdfUtils'
import { ScaleType, Scales } from './types'
import { calculateVisiblePage } from './viewUtils'

interface ExtendedPDFPageProxy extends PDFPageProxy {
  originalHeight: number
  originalWidth: number
}

export interface Props {
  url: string
  filename: string
  page?: number
  pagesToDisplay?: number[]
  initialScale?: ScaleType
  onPdfLoaded?: (pdf: PDFDocumentProxy) => void
}

export const PdfViewer = (props: Props) => {
  const { url, filename, pagesToDisplay = [], page = 0, initialScale = 'page-width' } = props
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [pages, setPages] = useState<ExtendedPDFPageProxy[]>([])
  const [scale, setScale] = useState(1)
  const toolbarElement = useRef<HTMLDivElement>(null)
  const containerElement = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const pageElements = useRef<(HTMLElement | null)[]>([])
  const totalPages = pagesToDisplay.length > 0 ? pagesToDisplay.length : pdf?.numPages

  useEffect(() => {
    goToPage(page)
  }, [page])

  const onPageFit = (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageHeight = pdfPage.originalHeight
    const containerHeight =
      parentElement.clientHeight - (toolbarElement.current?.offsetHeight || 0) - 20
    return containerHeight / pageHeight
  }

  const onPageWidth = (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageWidth = pdfPage.originalWidth
    const containerWidth = parentElement.clientWidth - 20
    return containerWidth / pageWidth
  }

  const onPageAdjust = (
    value: ScaleType,
    callback: (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement) => number
  ) => {
    const pageIndex = currentPage - 1

    if (pageIndex < 0 || !pages.length) {
      return
    }

    const pdfPage = pages[pageIndex]

    const parentElement = containerElement.current
    if (parentElement && pdfPage) {
      const expectedScale = callback(pdfPage, parentElement)
      setScale(expectedScale)
    }
    setSelectedScale(value)
  }

  const setScaleFromValue = (value: ScaleType): void => {
    setScale(Number(value))
    setSelectedScale(value)
  }

  const defaultScale = initialScale || 'page-width'
  const [selectedScale, setSelectedScale] = useState<ScaleType>(defaultScale)

  const scales: Scales[] = [
    {
      label: i18n.t('ats.cv_viewer.page_width'),
      value: 'page-width',
      callback: (value: ScaleType) => onPageAdjust(value, onPageWidth),
    },
    {
      label: i18n.t('ats.cv_viewer.page_fit'),
      value: 'page-fit',
      callback: (value: ScaleType) => onPageAdjust(value, onPageFit),
    },
    {
      label: '50%',
      value: '0.5',
      callback: setScaleFromValue,
    },
    {
      label: '75%',
      value: '0.75',
      callback: setScaleFromValue,
    },
    {
      label: '100%',
      value: '1',
      callback: setScaleFromValue,
    },
    {
      label: '125%',
      value: '1.25',
      callback: setScaleFromValue,
    },
    {
      label: '150%',
      value: '1.5',
      callback: setScaleFromValue,
    },
    {
      label: '200%',
      value: '2',
      callback: setScaleFromValue,
    },
    {
      label: '300%',
      value: '3',
      callback: setScaleFromValue,
    },
    {
      label: '400%',
      value: '4',
      callback: setScaleFromValue,
    },
  ]

  useEffect(() => {
    if (initialScale === 'page-width' || initialScale === 'page-fit') {
      return
    }
    setScale(Number(initialScale))
  }, [initialScale])

  useEffect(() => {
    if (pages.length > 0 && (initialScale === 'page-width' || initialScale === 'page-fit')) {
      onChangeScale(initialScale)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length, initialScale])

  useEffect(() => {
    if (!containerElement.current) return

    const handleClick = (event: Event) => {
      const target = event.target
      if (target instanceof HTMLAnchorElement) {
        target.target = '_blank'
        target.rel = 'noopener noreferrer'
      }
    }

    const el = containerElement.current
    el.addEventListener('click', handleClick, true)

    return () => {
      el.removeEventListener('click', handleClick, true)
    }
  }, [])

  const onChangeScale = (scaleValue: ScaleType) => {
    const scaleOption = scales.find((s) => s.value === scaleValue)

    scaleOption?.callback(scaleValue)
  }

  const file = useMemo(
    () => ({
      url,
      withCredentials: true,
    }),
    [url]
  )

  useEffect(() => {
    setPdf(null)
    pageElements.current = []
  }, [url])

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setPdf(pdf)

    if (props.onPdfLoaded) props.onPdfLoaded(pdf)

    setTimeout(() => {
      setCurrentPage(page + 1)

      goToPage(page)
    }, 0)
  }

  const onContainerScroll = (event: React.BaseSyntheticEvent) => {
    const visiblePage = calculateVisiblePage(
      event,
      pageElements.current,
      toolbarElement.current?.offsetHeight || 0
    )

    if (!visiblePage) return

    setCurrentPage(visiblePage)
  }

  const increaseScale = () =>
    changeScaleToNextStep(scales, (scaleValue, scale) => scaleValue > scale)
  const decreaseScale = () =>
    changeScaleToNextStep([...scales].reverse(), (scaleValue, scale) => scaleValue < scale)

  const changeScaleToNextStep = (
    scaleOptions: typeof scales,
    compareScales: (scale: number, currentScale: number) => boolean
  ) => {
    const newScale = scaleOptions.find((scaleOption) => {
      const scaleValue = Number(scaleOption.value)

      return compareScales(scaleValue, scale)
    })

    newScale?.callback(newScale.value)
  }

  useUnsafeMemo(() => {
    if (!pdf) setPages([])
  }, [pdf])

  const pagesList = useUnsafeMemo(() => {
    if (!pdf) return null

    const style: React.CSSProperties & { [key: string]: number } = {
      '--scale-factor': scale,
    }

    return Array.from(Array(totalPages)).map((_, index) => (
      <div key={index} className={'AtsPdfViewer_pdfViewerPage'} style={style}>
        <Page
          renderForms
          renderTextLayer
          pageNumber={((pagesToDisplay.length > 0 ? pagesToDisplay[index] : index) || 0) + 1}
          scale={scale}
          inputRef={(reference) => {
            pageElements.current[index] = reference
          }}
          onLoadSuccess={(pdfPage: ExtendedPDFPageProxy) => {
            pages[index] = pdfPage
            setPages([...pages])
          }}
        />
      </div>
    ))
  }, [pdf, scale, pagesToDisplay])

  const goToPage = useUnsafeCallback(
    (targetPageIndex: number) => {
      if (!pdf) return

      let destinationPageIndex: number = targetPageIndex
      const lastPageIndex = (totalPages || 1) - 1

      if (destinationPageIndex > lastPageIndex) {
        destinationPageIndex = lastPageIndex
      } else if (destinationPageIndex < 0) {
        destinationPageIndex = 0
      }

      const element = pageElements.current[destinationPageIndex]
      const parentContainer = element?.offsetParent
      if (parentContainer) {
        parentContainer.scrollTop =
          element.offsetTop - (toolbarElement.current?.offsetHeight || 0) - 10
      }
    },
    [pdf]
  )

  const onPrintPdf = () => {
    printPdf(pdf)
  }

  const onDownloadPdf = () => {
    const downloadFilename = filename === '' ? 'document.pdf' : filename

    downloadPdf(pdf, downloadFilename)
  }

  const showSpinner = !pdf

  return (
    <div className={'AtsPdfViewer_pdfViewerContainer'}>
      <div
        className={'AtsPdfViewer_pdfViewerInternalContainer'}
        ref={containerElement}
        onScroll={onContainerScroll}
      >
        <Box
          ref={toolbarElement}
          className={'AtsPdfViewer_pdfViewerToolbar'}
          height="s64"
          background="grey100"
          padding="s16"
          justifyContent="spaceBetween"
          alignItems="center"
          flexDirection="row"
        >
          <Box gap="s8" flexDirection="row" alignItems="center">
            {pdf?.numPages && (
              <Text.Normal size="200" color="textSecondary" weight="medium">
                {currentPage} / {totalPages}
              </Text.Normal>
            )}
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.previous_page')}
              onClick={() => goToPage(currentPage - 2)}
              icon={ChevronUp}
            />
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.next_page')}
              onClick={() => goToPage(currentPage)}
              icon={ChevronDown}
            />
          </Box>

          <Box gap="s8" flexDirection="row" alignItems="center">
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.zoom_out')}
              onClick={decreaseScale}
              icon={Minus}
            />
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.zoom_in')}
              onClick={increaseScale}
              icon={Plus}
            />

            <F0Select
              hideLabel
              label={i18n.t('ats.cv_viewer.scale_selector')}
              options={scales}
              onChange={(selectedScale: ScaleType) => onChangeScale(selectedScale)}
              value={selectedScale}
            />
          </Box>

          <Box gap="s8" flexDirection="row" alignItems="center">
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.print')}
              onClick={onPrintPdf}
              icon={Printer}
            />

            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.download')}
              onClick={onDownloadPdf}
              icon={Download}
            />
          </Box>
        </Box>

        {showSpinner && <LoadingSpinner />}

        {url && (
          <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<LoadingSpinner />}>
            {pagesList}
          </Document>
        )}
      </div>
    </div>
  )
}
